import { STUDIO_BUILDERS } from './builders';

export interface StudioRouteTarget {
  type: string;
  templateId: string;
  systemName: string;
  /** Present for a version-addressed type (`arc_template`) — absent callers fall through to
   * the type's other routing (the browse drawer for arc_template). */
  version?: number;
}

/**
 * Per-type route shape for the types an active builder owns. The Space builder aliases
 * `/studio/composition/:name` (by system name, matching the existing `ComposeTargetPage` route)
 * alongside `/studio/space/:id` (by template id, matching the metaspace/space-document route) —
 * one builder, two id schemes, because the two backing routes predate the unified template
 * registry. Types without an entry here fall through to the generic `/studio/<segment>/<id>`
 * shape below.
 */
const ROUTE_BUILDERS_BY_TYPE: Record<string, (ref: StudioRouteTarget) => string | null> = {
  metaspace: (ref) => `/studio/space/${ref.templateId}`,
  composition: (ref) => `/studio/composition/${ref.systemName}`,
  // Contexts have no template-id-keyed route (`ContextEditorPage` addresses by
  // `system_name` only, matching `/contexts/:systemName/edit` today) — the generic
  // `templateId` fallback below would produce a route the editor can't resolve.
  context: (ref) => `/studio/context/${ref.systemName}`,
  // The Sphere Type Editor (EAINT-892) addresses by `schema_id`. `schema` sits on the
  // `browse`-status Approach builder alongside `approach` (still drawer-only) — this entry is
  // checked before the builder-wide browse/active gate below, so this one type gets a real
  // editor route while the rest of that builder's types keep the drawer.
  schema: (ref) => `/studio/types/${ref.templateId}`,
  // The Arc Editor Canvas (EAINT-951) addresses by `systemName`/`version`, like the Sphere Type
  // Editor sits on an otherwise `browse`-status builder. Unlike `schema`, a ref can arrive with
  // no known version (e.g. a card whose lifecycle is unresolved) — returning `null` here (not a
  // string) falls through to the drawer's query-param shape below, rather than emitting a route
  // the editor can't resolve.
  arc_template: (ref) =>
    ref.version != null
      ? `/studio/arc/${encodeURIComponent(ref.systemName)}/${ref.version}`
      : null,
};

/**
 * Canonical Studio route for a `DeskTemplateRef`/finding `target`, per EAINT-865's ratified
 * decision: the server (`buildStudioRoute` in schema-service) only knows type + ids, not
 * `STUDIO_BUILDERS`' segments/aliases — this is the authoritative source and overrides the
 * server-supplied `route` field. Returns `null` when no builder with a real destination owns the
 * type (a scaffolded builder, or a type with no builder at all) — callers fall back to the
 * Registry strip rather than navigating into a builder that isn't real.
 *
 * A `ROUTE_BUILDERS_BY_TYPE` entry wins over the owning builder's overall status — a `browse`
 * builder can still own one type with a genuine per-item route (EAINT-892: `schema` on the
 * otherwise-drawer-only Approach builder). Absent an entry, `browse` builders fall to the
 * drawer's `?template=<systemName>&type=<type>` query shape — the `type` is not decoration:
 * `system_name` is unique per `(org, system_name)` PER TYPE TABLE, and the Approach builder's
 * gallery carries both `approach` and `schema`, so a systemName alone can address two different
 * cards. `active` builders with no entry fall to the generic `/studio/<segment>/<templateId>`.
 */
export function studioRouteForTemplate(ref: StudioRouteTarget): string | null {
  const builder = STUDIO_BUILDERS.find((candidate) => candidate.templateTypes?.includes(ref.type));
  if (!builder) return null;

  const routeBuilder = ROUTE_BUILDERS_BY_TYPE[ref.type];
  if (routeBuilder) {
    const built = routeBuilder(ref);
    if (built !== null) return built;
  }

  if (builder.status === 'browse') {
    return `/studio/${builder.segment}?template=${encodeURIComponent(ref.systemName)}&type=${encodeURIComponent(ref.type)}`;
  }
  if (builder.status !== 'active') return null;

  return `/studio/${builder.segment}/${ref.templateId}`;
}
